import { useEffect, useRef, useState } from "react";
import { SERVER_URL } from "../../../config/env";
import { tokenService } from "../../../services/tokenService";
import { useAppDispatch } from "../../../store/hooks";
import { applyQuizSocketMessage, isQuizSocketMessage, refetchQuiz } from "../api/quizSocket";
import type { WaitingRoomRacer } from "../api/types";

// Keepalive cadence. The proxy in front of the backend (NPM) enforces a 90s
// proxy_read_timeout, and the image-generation step is WS-silent for ~100s —
// without traffic the proxy kills the socket mid-generation. The server
// answers {type:"ping"} with a pong frame (raceWebSocket.ts).
const PING_INTERVAL_MS = 25_000;

export interface RaceSocketState {
  participantCount?: number;
  participantEmojis?: string[];
  /** Racer strip (emoji + no-emoji penalty), sent on `race:state` frames. */
  racers?: WaitingRoomRacer[];
  phase?: string;
  generationStatus?: string;
  message?: string;
  // Progress-event fields (identity-neutral).
  totalSteps?: number;
  stepIndex?: number;
  stepName?: string;
  label?: string;
  percent?: number;
  /** Time spent INSIDE the current step so far (heartbeats only; optional on
   *  the wire — older backends don't send it). Re-anchored per step. */
  elapsedMs?: number;
  artId?: string;
  // Live image-generation preview: the latest partial PNG (base64) of today's
  // art as it renders, plus its index. Cosmetic — cleared on start/finish.
  previewB64?: string;
  previewIndex?: number;
}

export interface UseAotdRaceSocketResult {
  connected: boolean;
  state: RaceSocketState;
}

/**
 * Reduce an incoming WS event into a fresh socket state. Each event type is
 * handled explicitly so that completion/failure payloads fully replace stale
 * phase/message fields instead of leaking them via partial spread.
 */
function reduceSocketState(prev: RaceSocketState, msg: any): RaceSocketState {
  const type = typeof msg?.type === "string" ? msg.type : "";

  switch (type) {
    case "aotd.progress.started":
      return {
        ...prev,
        generationStatus: "generating",
        totalSteps: typeof msg.totalSteps === "number" ? msg.totalSteps : prev.totalSteps,
        stepIndex: 0,
        stepName: undefined,
        label: undefined,
        percent: 0,
        elapsedMs: undefined,
        message: undefined,
        // Fresh run — drop any stale preview from a previous generation.
        previewB64: undefined,
        previewIndex: undefined,
      };

    case "aotd.preview":
      return {
        ...prev,
        generationStatus: "generating",
        previewB64: typeof msg.previewB64 === "string" ? msg.previewB64 : prev.previewB64,
        previewIndex:
          typeof msg.previewIndex === "number" ? msg.previewIndex : prev.previewIndex,
      };

    case "aotd.progress.step":
      return {
        ...prev,
        generationStatus: "generating",
        totalSteps: typeof msg.totalSteps === "number" ? msg.totalSteps : prev.totalSteps,
        stepIndex: typeof msg.stepIndex === "number" ? msg.stepIndex : prev.stepIndex,
        stepName: typeof msg.stepName === "string" ? msg.stepName : undefined,
        label: typeof msg.label === "string" ? msg.label : undefined,
        percent: typeof msg.percent === "number" ? msg.percent : prev.percent,
        // Re-anchored per step — never carried over from the previous step.
        elapsedMs: typeof msg.elapsedMs === "number" ? msg.elapsedMs : undefined,
        message: typeof msg.label === "string" ? msg.label : prev.message,
        // Surface the human label through `phase` so existing UI consumers
        // that bind to phase pick up the step transition without changes.
        phase: typeof msg.label === "string" ? msg.label : prev.phase,
      };

    case "aotd.progress.completed":
      return {
        ...prev,
        percent: 100,
        label: undefined,
        stepName: undefined,
        elapsedMs: undefined,
      };

    case "pong":
    case "latency:ping":
      // Keepalive reply / server latency probe — swallow without producing a
      // new state object so neither cadence triggers pointless re-renders.
      // (The latency:ping echo itself is sent from the onmessage handler,
      // before this reducer runs — it must not wait on a React render.)
      return prev;

    case "aotd.completed":
      return {
        participantCount: undefined,
        participantEmojis: undefined,
        racers: undefined,
        phase: undefined,
        generationStatus: "completed",
        message: undefined,
        totalSteps: undefined,
        stepIndex: undefined,
        stepName: undefined,
        label: undefined,
        percent: 100,
        artId: typeof msg.artId === "string" ? msg.artId : undefined,
      };

    case "aotd.failed":
      return {
        participantCount: prev.participantCount,
        participantEmojis: prev.participantEmojis,
        racers: prev.racers,
        phase: undefined,
        generationStatus: "failed",
        message: typeof msg.reason === "string" ? msg.reason : undefined,
      };

    case "state:changed":
    case "participant:joined":
    case "published":
    case "race:state":
    default:
      return {
        ...prev,
        ...(typeof msg.participantCount === "number" ? { participantCount: msg.participantCount } : {}),
        ...(Array.isArray(msg.participantEmojis) ? { participantEmojis: msg.participantEmojis } : {}),
        ...(Array.isArray(msg.racers) ? { racers: msg.racers } : {}),
        ...(typeof msg.phase === "string" ? { phase: msg.phase } : {}),
        ...(typeof msg.generationStatus === "string" ? { generationStatus: msg.generationStatus } : {}),
        ...(typeof msg.message === "string" ? { message: msg.message } : {}),
      };
  }
}

function deriveWsBaseUrl(): string {
  if (SERVER_URL) {
    return SERVER_URL.replace(/^http/, "ws");
  }
  if (typeof window !== "undefined" && window.location) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}`;
  }
  return "ws://localhost";
}

/**
 * @param authKey  A value that changes when the user's auth identity becomes
 *   available (e.g. `isAuthenticated`). The race socket connects on mount with
 *   whatever token `tokenService` can hand back *right then* — and on a fresh
 *   page load that's often `null`, because auth bootstrap hasn't finished. A
 *   socket that opened anonymously is invisible to the backend's latency
 *   sampler (`raceWebSocket.ts` skips ping sampling when `username` is null),
 *   so that user races on raw arrival instead of latency-corrected time. Wiring
 *   `authKey` into the effect deps tears down the anonymous socket and
 *   reconnects *with* the token the moment auth is ready; the server keeps the
 *   latency bucket keyed by `dayKey:username`, so the reconnect just resumes
 *   sampling well before unlock. Token rotations don't flip `isAuthenticated`,
 *   so this reconnects at most once per login — no churn.
 */
export function useAotdRaceSocket(
  enabled: boolean,
  onEvent?: (event: { type: string } & RaceSocketState) => void,
  authKey?: string | number | boolean | null,
): UseAotdRaceSocketResult {
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<RaceSocketState>({});
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const backoffRef = useRef<number>(1000);
  const onEventRef = useRef(onEvent);
  // Emoji quiz frames go straight to the quiz board's RTK cache (quizSocket.ts)
  // and never touch the race state. Any socket after the first one is a
  // reconnect, and a reconnect refetches the board for what it missed.
  const dispatch = useAppDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    function stopPing(): void {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    }

    async function connect(): Promise<void> {
      if (cancelled) return;
      const token = await tokenService.getValidAccessToken().catch(() => null);
      const base = deriveWsBaseUrl();
      const params = new URLSearchParams();
      if (token) params.set("token", token);
      const url = `${base}/ws/aotd/race${params.toString() ? `?${params.toString()}` : ""}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        backoffRef.current = 1000;
        setConnected(true);
        if (hasOpenedRef.current) dispatchRef.current(refetchQuiz());
        hasOpenedRef.current = true;
        stopPing();
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          try {
            ws.send(JSON.stringify({ type: "ping" }));
          } catch {
            /* ignore — onclose handles the reconnect */
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(ev.data);
          if (!msg || typeof msg !== "object") return;
          // Server latency probe (aotdrace-2.0): echo IMMEDIATELY, before any
          // state work — the server computes RTT from this round-trip and the
          // race comparator subtracts the resulting one-way estimate from our
          // arrival time. Every ms spent here is a ms charged to our latency.
          if (msg.type === "latency:ping") {
            try {
              ws.send(JSON.stringify({ type: "latency:pong", seq: msg.seq }));
            } catch {
              /* ignore — onclose handles the reconnect */
            }
            return;
          }
          if (isQuizSocketMessage(msg)) {
            dispatchRef.current(applyQuizSocketMessage(msg));
            return;
          }
          setState((prev) => reduceSocketState(prev, msg));
          if (onEventRef.current && typeof msg.type === "string") {
            onEventRef.current(msg);
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onclose = () => {
        stopPing();
        if (cancelled) return;
        setConnected(false);
        wsRef.current = null;
        const delay = Math.min(backoffRef.current, 15000);
        backoffRef.current = Math.min(backoffRef.current * 2, 15000);
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
    }

    // Mobile foreground recovery: iOS Safari and backgrounded Chrome suspend
    // the socket (and mobile radios drop idle TCP via NAT timeout), often
    // without a prompt onclose. When the page returns to the foreground, if we
    // don't have a live socket, re-arm immediately instead of waiting out the
    // reconnect backoff — the ping burst needs to refill the latency window
    // before the user taps, or they race on raw arrival.
    function onVisibility(): void {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      // Only re-arm when there's no live socket. A socket that is OPEN,
      // CONNECTING, or even CLOSING is left alone — racing a connect() against a
      // closing socket's onclose (which schedules its own reconnect) would
      // orphan one of the two. After a real close, onclose nulls wsRef, so the
      // common post-suspension state here is wsRef.current === null.
      if (ws && ws.readyState !== WebSocket.CLOSED) return;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      backoffRef.current = 1000;
      void connect();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    void connect();

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      stopPing();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
      setConnected(false);
    };
  }, [enabled, authKey]);

  return { connected, state };
}
